import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { FolderOpen, Folder, Layers, Search, ChevronsUpDown } from "lucide-react";
import { cn } from "@shadcn-in-nx/lib/utils";
import { Skeleton } from "@shadcn-in-nx/ui/skeleton";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "@shadcn-in-nx/ui/popover";
import { Input } from "@shadcn-in-nx/ui/input";
import WorkflowPopoverList from "./WorkflowPopoverList";

const VISIBLE_COUNT = 4;

const folderClasses = (isActive) =>
    cn(
        "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all border",
        isActive
            ? "bg-primary text-primary-foreground border-primary shadow-sm"
            : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
    );

const badgeClasses = (isActive) =>
    cn(
        "text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center",
        isActive
            ? "bg-primary-foreground/20 text-primary-foreground"
            : "bg-gray-100 text-gray-500"
    );

function WorkflowFolders({ selectedWorkflowId, onSelect }) {
    const { t } = useTranslation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [desktopOpen, setDesktopOpen] = useState(false);
    const { call, response, isLoading } = useBluelagoon({
        method: "getFileWorkflows",
    });

    useEffect(() => { call(); }, [call]);

    const workflows = useMemo(() => response?.workflows || [], [response?.workflows]);

    const selectedName = useMemo(
        () => workflows.find((w) => w.id === selectedWorkflowId)?.name,
        [workflows, selectedWorkflowId]
    );

    const { visible, hasMore } = useMemo(() => {
        if (workflows.length <= VISIBLE_COUNT) return { visible: workflows, hasMore: false };
        const top = workflows.slice(0, VISIBLE_COUNT);
        const selectedInTop = !selectedWorkflowId || top.some((w) => w.id === selectedWorkflowId);
        if (selectedInTop) return { visible: top, hasMore: true };
        const selected = workflows.find((w) => w.id === selectedWorkflowId);
        return { visible: [...top.slice(0, VISIBLE_COUNT - 1), selected], hasMore: true };
    }, [workflows, selectedWorkflowId]);

    if (isLoading) {
        return <Skeleton className="h-9 w-[200px] rounded-lg sm:w-[480px]" />;
    }

    if (workflows.length === 0) return null;

    const handleSelect = (id, setOpen) => {
        onSelect(id);
        setOpen(false);
    };

    return (
        <div className="pb-2">
            {/* Mobile: single dropdown */}
            <div className="sm:hidden">
                <Popover open={mobileOpen} onOpenChange={setMobileOpen}>
                    <PopoverTrigger asChild>
                        <button className={cn(
                            "flex items-center gap-2 w-full justify-between px-3 py-2 rounded-lg text-sm font-medium border transition-all",
                            selectedWorkflowId
                                ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                : "bg-white text-gray-600 border-gray-200"
                        )}>
                            <span className="flex items-center gap-2">
                                {selectedWorkflowId ? <FolderOpen className="h-4 w-4" /> : <Layers className="h-4 w-4" />}
                                {selectedName || t("files.folders.all")}
                            </span>
                            <ChevronsUpDown className="h-3.5 w-3.5" />
                        </button>
                    </PopoverTrigger>
                    <WorkflowPopoverList
                        workflows={workflows}
                        selectedWorkflowId={selectedWorkflowId}
                        onSelect={(id) => handleSelect(id, setMobileOpen)}
                        className="w-[var(--radix-popover-trigger-width)]"
                    />
                </Popover>
            </div>

            {/* Desktop: folder tabs + optional "more" dropdown */}
            <div className="hidden sm:flex gap-2 items-center flex-wrap">
                <button onClick={() => onSelect(null)} className={folderClasses(!selectedWorkflowId)}>
                    <Layers className="h-4 w-4" />
                    {t("files.folders.all")}
                </button>

                {visible.map((wf) => {
                    const isActive = selectedWorkflowId === wf.id;
                    return (
                        <button key={wf.id} onClick={() => onSelect(wf.id)} className={folderClasses(isActive)}>
                            {isActive ? <FolderOpen className="h-4 w-4" /> : <Folder className="h-4 w-4" />}
                            <span className="truncate max-w-[160px]">{wf.name}</span>
                            <span className={badgeClasses(isActive)}>{wf.fileCount}</span>
                        </button>
                    );
                })}

                {hasMore && (
                    <Popover open={desktopOpen} onOpenChange={setDesktopOpen}>
                        <PopoverTrigger asChild>
                            <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-dashed border-gray-300 text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition-all">
                                <ChevronsUpDown className="h-3.5 w-3.5" />
                                {t("files.folders.more", { count: workflows.length - VISIBLE_COUNT })}
                            </button>
                        </PopoverTrigger>
                        <WorkflowPopoverList
                            workflows={workflows}
                            selectedWorkflowId={selectedWorkflowId}
                            onSelect={(id) => handleSelect(id, setDesktopOpen)}
                            className="w-64"
                        />
                    </Popover>
                )}
            </div>
        </div>
    );
}

export default WorkflowFolders;
